import { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import logger from '../utils/logger';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import xpService from '../services/xpService';
import userService from '../services/userService';

/**
 * useAuth - Manages all authentication state and operations.
 *
 * Returns auth state and handlers for login, signup, logout,
 * email verification, passwordless login, and password reset.
 */
export const useAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [browsingMode, setBrowsingMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState('');
  const [showSignup, setShowSignup] = useState(false);

  // Form state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({ username: '', email: '', password: '', gender: '', displayPreference: 'marketcap' });

  // Enhanced auth states
  const [authMode, setAuthMode] = useState('login');
  const [emailForPasswordless, setEmailForPasswordless] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [lastCreatedUserEmail, setLastCreatedUserEmail] = useState('');
  const [lastCreatedUserPassword, setLastCreatedUserPassword] = useState('');

  // Rank up state (tracked here because it's set during auth)
  const [hasPendingRankUp, setHasPendingRankUp] = useState(false);

  // Following/blocked state (set during auth state change)
  const [following, setFollowing] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);

  // Check for email link sign-in on page load
  useEffect(() => {
    const handleEmailLinkSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }

        try {
          await signInWithEmailLink(auth, email, window.location.href);
          localStorage.removeItem('emailForSignIn');
          setEmailLinkSent(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
          logger.error('Error signing in with email link:', err);
          setError('Error signing in with email link: ' + err.message);
        }
      }
    };

    handleEmailLinkSignIn();
  }, []);

  // Check authentication state
  useEffect(() => {
    let xpUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCurrentUser({
            uid: user.uid,
            ...userData,
            banner: userData.banner || null,
            emailVerified: user.emailVerified,
          });

          // Subscribe to real-time XP updates
          xpUnsubscribe = xpService.subscribeToUserXP(user.uid, (xpData) => {
            if (!xpData.error) {
              setCurrentUser((prev) => ({
                ...prev,
                xp: xpData.xp,
                successfulCalls: xpData.successfulCalls,
                totalCalls: xpData.totalCalls,
                winRate: xpData.winRate,
              }));
            }
          });

          setFollowing(userData.following || []);
          setBlockedUsers(userData.blockedUsers || []);
          setIsLoggedIn(true);
          setBrowsingMode(false);

          // Update email verification status in database
          if (user.emailVerified && !userData.emailVerified) {
            await updateDoc(doc(db, 'users', user.uid), { emailVerified: true });
          }

          // Check for pending rank up
          try {
            const pending = await userService.hasPendingRankUp(user.uid);
            setHasPendingRankUp(pending);
          } catch (err) {
            logger.error('Error checking pending rank up:', err);
          }
        }
      } else {
        if (!browsingMode) {
          setIsLoggedIn(false);
          setCurrentUser(null);
          setHasPendingRankUp(false);

          if (xpUnsubscribe) {
            xpUnsubscribe();
          }
        }
      }
      setAuthLoading(false);
    });

    return () => {
      unsubscribe();
      if (xpUnsubscribe) {
        xpUnsubscribe();
      }
      xpService.cleanup();
    };
  }, [browsingMode]);

  const handleSignup = async () => {
    setError('');

    if (!signupForm.gender) {
      setError('Please select your gender');
      return;
    }

    if (!signupForm.username || !signupForm.email || !signupForm.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupForm.email,
        signupForm.password
      );

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        username: signupForm.username,
        handle: `@${signupForm.username}`,
        email: signupForm.email,
        gender: signupForm.gender,
        bio: '',
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${signupForm.username}`,
        following: [],
        followers: [],
        xp: 0,
        createdAt: serverTimestamp(),
      });

      setSignupForm({ username: '', email: '', password: '', gender: '', displayPreference: 'marketcap' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogin = async () => {
    setError('');

    if (!loginForm.email || !loginForm.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      setLoginForm({ email: '', password: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePasswordlessLogin = async () => {
    setError('');

    if (!loginForm.email) {
      setError('Please enter your email');
      return;
    }

    try {
      const actionCodeSettings = {
        url: window.location.origin + '/auth',
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, loginForm.email, actionCodeSettings);
      localStorage.setItem('emailForSignIn', loginForm.email);
      setEmailLinkSent(true);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePasswordReset = async () => {
    setError('');

    if (!loginForm.email) {
      setError('Please enter your email address');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, loginForm.email);
      setResetEmailSent(true);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSignupWithoutVerification = async () => {
    setError('');

    if (!signupForm.gender) {
      setError('Please select your gender');
      return;
    }

    if (!signupForm.username || !signupForm.email || !signupForm.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signupForm.email,
        signupForm.password
      );

      const userData = {
        uid: userCredential.user.uid,
        username: signupForm.username,
        handle: '@' + signupForm.username.toLowerCase().replace(/[^a-z0-9]/g, ''),
        email: signupForm.email,
        gender: signupForm.gender,
        displayPreference: signupForm.displayPreference || 'price',
        bio: '',
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${signupForm.username}`,
        banner: '',
        blockedUsers: [],
        following: [],
        followers: [],
        createdAt: serverTimestamp(),
        emailVerified: true,
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      setSignupForm({ username: '', email: '', password: '', gender: '', displayPreference: 'marketcap' });
      setShowSignup(false);
      setAuthMode('login');
      setError('✅ Account created successfully! Welcome to TopBlast!');
    } catch (err) {
      logger.error('Signup error:', err);
      setError(err.message);
    }
  };

  const resendEmailVerification = async () => {
    try {
      let user = auth.currentUser;

      if (!user && lastCreatedUserEmail && lastCreatedUserPassword) {
        try {
          const tempCredential = await signInWithEmailAndPassword(
            auth,
            lastCreatedUserEmail,
            lastCreatedUserPassword
          );
          user = tempCredential.user;
        } catch (signInError) {
          logger.error('Temporary sign-in failed:', signInError);
          setError('Unable to resend verification email. Please try logging in first.');
          return;
        }
      }

      if (user) {
        try {
          const actionCodeSettings = {
            url: 'https://topblast.tech',
            handleCodeInApp: false,
          };
          await sendEmailVerification(user, actionCodeSettings);
        } catch (primaryError) {
          await sendEmailVerification(user);
        }

        if (lastCreatedUserEmail && lastCreatedUserPassword) {
          await auth.signOut();
        }

        setEmailVerificationSent(true);
        setError(`✅ Verification email resent to ${user.email}. Please check your inbox and spam folder.`);
      } else if (loginForm.email) {
        setError('Please enter your password and try logging in first, then we can resend the verification email.');
      } else {
        setError('Please enter your email address and try logging in first.');
      }
    } catch (err) {
      logger.error('Email verification error:', err);
      if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Please log in again to resend verification email.');
      } else if (err.code === 'auth/user-not-found') {
        setError('Account not found. Please check the email address.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try logging in first.');
      } else {
        setError(`Error sending verification: ${err.message}`);
      }
    }
  };

  const handleLogout = async () => {
    try {
      if (currentUser?.uid) {
        xpService.stopMonitoring(currentUser.uid);
      }

      await signOut(auth);
      setIsLoggedIn(false);
      setCurrentUser(null);
    } catch (err) {
      logger.error('Logout error:', err);
    }
  };

  const handleBrowseWithoutAccount = () => {
    setBrowsingMode(true);
    setIsLoggedIn(true);
    setCurrentUser(null);
    setBlockedUsers([]);
    setFollowing([]);
    setError('');
  };

  return {
    // State
    isLoggedIn,
    authLoading,
    browsingMode,
    currentUser,
    setCurrentUser,
    error,
    setError,
    showSignup,
    setShowSignup,
    loginForm,
    setLoginForm,
    signupForm,
    setSignupForm,
    authMode,
    setAuthMode,
    emailForPasswordless,
    setEmailForPasswordless,
    resetEmailSent,
    emailLinkSent,
    emailVerificationSent,
    hasPendingRankUp,
    setHasPendingRankUp,
    following,
    setFollowing,
    blockedUsers,
    setBlockedUsers,

    // Handlers
    handleSignup,
    handleLogin,
    handlePasswordlessLogin,
    handlePasswordReset,
    handleSignupWithoutVerification,
    resendEmailVerification,
    handleLogout,
    handleBrowseWithoutAccount,
  };
};
